import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import type { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import type { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiContextStore } from '../state/api-context.store';
import { AuthSessionRecoveryStore } from './auth-session-recovery.store';
import { AuthStore } from './auth.store';

const AUTH_RETRY_ATTEMPTED = new HttpContextToken(() => false);

export function authSessionInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authStore = inject(AuthStore);
  const apiContext = inject(ApiContextStore);
  const recovery = inject(AuthSessionRecoveryStore);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!shouldRecover(req, error)) {
        return throwError(() => error);
      }

      return authStore.loadSession().pipe(
        switchMap((session) => {
          if (!session) {
            recovery.requestReauth(currentReturnUrl());
            return throwError(() => error);
          }

          recovery.clear();
          return next(
            req.clone({
              context: req.context.set(AUTH_RETRY_ATTEMPTED, true),
              setHeaders: {
                ...apiContext.headers(),
              },
            }),
          );
        }),
        catchError((refreshError: unknown) => {
          recovery.requestReauth(currentReturnUrl());
          return throwError(() => refreshError);
        }),
      );
    }),
  );
}

/** The app's OWN session-expiry response — the only 401 that means "the Automato session died."
 *  Other domains reuse HTTP 401 for unrelated auth failures (e.g. `provider_auth_failed` when an
 *  Instagram/WhatsApp/Google OAuth code exchange fails, `provider_auth_invalid` for telephony,
 *  `webhook_signature_invalid`) — those must never trigger a reauth prompt or a same-body retry,
 *  or a single-use authorization code gets resubmitted to the provider a second time and fails with
 *  a confusing "already used" error even when the original attempt actually succeeded. */
const SESSION_EXPIRED_ERROR_CODE = 'unauthorized';

function shouldRecover(req: HttpRequest<unknown>, error: unknown) {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    isSessionExpiredBody(error.error) &&
    isApiUrl(req.url) &&
    !isAuthUrl(req.url) &&
    !req.context.get(AUTH_RETRY_ATTEMPTED)
  );
}

function isSessionExpiredBody(body: unknown): boolean {
  return !!body && typeof body === 'object' && (body as { error?: unknown }).error === SESSION_EXPIRED_ERROR_CODE;
}

function isApiUrl(url: string) {
  return url.startsWith(environment.apiUrl);
}

function isAuthUrl(url: string) {
  return url.includes('/v1/auth/');
}

function currentReturnUrl() {
  return `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
}
